import { useParams } from 'react-router-dom'
import { useEvent } from '../hooks/useEvent'
import { useInviteProfile } from '../hooks/useInviteProfile'
import { useInviteResponse } from '../hooks/useInviteResponse'
import { usePaymentIntent } from '../hooks/usePaymentIntent'
import { useAuthContext } from '../hooks/useAuthContext'
import {
  EventCover,
  GuestCountBar,
  InfoRow,
  DescriptionBlock,
  Divider,
  FloatingOverlay,
  GuestListHeader,
  GuestListItem,
} from '@shoots/ui'
import { posthogService } from '../services/posthogService'
import { Header } from '../components/layout/Header'
import { RSVPFlow } from './RSVPFlow'
import { formatPrice } from '../utils/formatPrice'
import './event-page.css'

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatTime(dateString: string) {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function formatTimeRange(start: string, end: string) {
  return `${formatTime(start)} – ${formatTime(end)}`
}

function getDayAbbr(dateString: string) {
  return new Date(dateString)
    .toLocaleDateString('en-US', { weekday: 'short' })
    .toUpperCase()
}

function getDayNum(dateString: string) {
  return new Date(dateString).getDate().toString()
}

function mapRsvpStatus(
  response: 'yes' | 'no' | null
): 'unanswered' | 'confirmed' | 'cant-go' {
  if (response === 'yes') return 'confirmed'
  if (response === 'no') return 'cant-go'
  return 'unanswered'
}

export function EventPage() {
  const { eventId, inviteId } = useParams<{
    eventId?: string
    inviteId?: string
  }>()
  const {
    user,
    authenticated,
    loading: authLoading,
    sendOTP,
    verifyOTP,
    error: authError,
  } = useAuthContext()
  const { event, loading, error, refreshEvent } = useEvent({
    eventId,
    authenticated,
  })
  const {
    invite,
    profile,
    loading: inviteProfileLoading,
    error: inviteProfileError,
    refetch: refetchInvite,
    updateInviteResponse,
  } = useInviteProfile(
    inviteId || (event?.invite_id ?? undefined),
    authenticated
  )
  const { respondToInvite, responding, responseError } = useInviteResponse()

  // Treat the signed-in user as the invitee when either signal confirms it:
  //   1. The gathering endpoint echoed back their invite_id, or
  //   2. The invite loaded from the URL is linked to a profile owned by them
  //      (profile.auth_id === user.id). This guards against the gathering
  //      endpoint's auth detection missing on refresh, which would otherwise
  //      hide the panel from a legitimate invitee.
  // The URL inviteId alone is NOT proof — a signed-in non-invitee opening
  // someone else's link must not see the RSVP panel.
  // Computed before early-return guards so usePaymentIntent — also a hook —
  // can be called unconditionally on every render.
  const userOwnsInvite = !!user && profile?.auth_id === user.id
  const resolvedInviteId = authenticated
    ? (event?.invite_id ?? (userOwnsInvite ? invite?.id ?? inviteId : undefined))
    : invite?.id || inviteId

  const paymentIntent = usePaymentIntent({
    eventId,
    inviteId: resolvedInviteId,
    myPaymentStatus: event?.my_payment_status,
    eventPrice: event?.price,
    authenticated,
    inviteResponse: invite?.response,
  })

  // --- Loading ---
  if (loading || inviteProfileLoading || authLoading) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-action-primary)]" />
        </div>
      </>
    )
  }

  // --- Error ---
  if (!eventId || error || inviteProfileError || !event) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-[var(--color-text-secondary)]">
            {error || inviteProfileError || 'Event not found.'}
          </p>
        </div>
      </>
    )
  }

  // --- Derived data ---
  const rsvpStatus = mapRsvpStatus(invite?.response ?? null)

  const hostName = `${event.creator.first_name} ${event.creator.last_name}`
  const otherGuests = event.invited_contacts.filter(
    c => !profile || c.id !== profile.id
  )
  const goingGuests = otherGuests.filter(c => c.response === 'yes')
  const cantGoGuests = otherGuests.filter(c => c.response === 'no')
  const goingCount = goingGuests.length + (invite?.response === 'yes' ? 1 : 0)
  const cantGoCount = cantGoGuests.length + (invite?.response === 'no' ? 1 : 0)
  const avatars = otherGuests
    .filter(c => c.response === 'yes')
    .map(c => ({
      name: `${c.first_name} ${c.last_name}`,
      src: c.image_path ?? undefined,
    }))

  const isCreator = event.canEdit

  const rsvpTitles: Record<'unanswered' | 'confirmed' | 'cant-go', string> = {
    unanswered: "You're in! Will you make it?",
    confirmed: "You're going!",
    'cant-go': 'Maybe next time',
  }
  const rsvpSubtitles: Record<
    'unanswered' | 'confirmed' | 'cant-go',
    string | undefined
  > = {
    unanswered: undefined,
    confirmed: `${formatDate(event.date)} · ${formatTime(event.date)}`,
    'cant-go': "We'll let the host know",
  }
  const rsvpStatusLabels: Record<
    'unanswered' | 'confirmed' | 'cant-go',
    string | undefined
  > = {
    unanswered: undefined,
    confirmed: 'RSVP confirmed',
    'cant-go': 'Declined',
  }

  // --- RSVP handler ---
  const handleInviteResponse = async (response: 'confirmed' | 'cant-go') => {
    const mapped = response === 'confirmed' ? 'yes' : 'no'
    if (!resolvedInviteId) return
    updateInviteResponse(mapped)
    try {
      const result = await respondToInvite(resolvedInviteId, mapped)
      if (!result?.success) {
        await refetchInvite()
        return
      }
      posthogService.group('invite', resolvedInviteId)
      posthogService.capture('rsvp_response', {
        groups: { invite: resolvedInviteId },
        response: mapped,
        event_id: eventId,
        invite_id: resolvedInviteId,
      })
      // 'yes' path: the optimistic updateInviteResponse('yes') above will flip
      // invite.response to 'yes', which triggers usePaymentIntent's auto-fire.
      // No explicit refetch needed.
      if (mapped === 'no') {
        paymentIntent.cancel()
      }
    } catch {
      await refetchInvite()
    }
  }

  // --- Icons ---
  const dateIcon = (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <span
        style={{
          fontFamily: 'var(--font-family-body)',
          fontSize: 9,
          fontWeight: 700,
          textTransform: 'uppercase',
          lineHeight: 1,
          color: 'var(--color-input-text-secondary)',
        }}
      >
        {getDayAbbr(event.date)}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-family-heading)',
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1,
          color: 'var(--color-input-text)',
        }}
      >
        {getDayNum(event.date)}
      </span>
    </div>
  )
  const priceIcon = (
    <span
      style={{
        fontFamily: 'var(--font-family-heading)',
        fontSize: 20,
        fontWeight: 600,
        lineHeight: 1,
        color: 'var(--color-green-600)',
      }}
    >
      $
    </span>
  )

  // --- Panel ---
  // Show the panel when: not authenticated (sign-in flow), or authenticated with an invite
  const showPanel = !authenticated || !!resolvedInviteId
  const floatingPanel = (
    <RSVPFlow
      authenticated={authenticated}
      rsvpStatus={rsvpStatus}
      rsvpTitle={rsvpTitles[rsvpStatus]}
      rsvpSubtitle={rsvpSubtitles[rsvpStatus]}
      rsvpStatusLabel={rsvpStatusLabels[rsvpStatus]}
      initialPhone={profile?.phone_number}
      authError={authError}
      onSendOTP={sendOTP}
      onVerifyOTP={async (phone, code) => {
        await verifyOTP(phone, code)
      }}
      onResendOTP={sendOTP}
      onStatusChange={handleInviteResponse}
      onPaymentSuccess={refreshEvent}
      responding={responding}
      responseError={responseError}
      eventPrice={event.price}
      myPaymentStatus={event.my_payment_status}
      paymentClientSecret={paymentIntent.clientSecret}
      paymentError={paymentIntent.error}
      breakdown={paymentIntent.breakdown}
      onMethodChange={method => paymentIntent.refetch(method)}
    />
  )

  // --- Cover props ---
  // Desktop: no src → centered XL avatar variant; the image is applied
  // via CSS background-image on .event-page-cover-desktop instead.
  // Mobile: full props → bottom-left variant with image.
  const baseCoverProps = {
    title: event.title,
    hostName,
    hostAvatarSrc: event.creator.image_path ?? undefined,
  }
  const imageUrl = event.image_url || undefined

  // --- Layout ---
  // Two EventCover instances: one inside FloatingOverlay (mobile),
  // one outside as a sibling (desktop sticky). CSS toggles visibility.
  // This keeps the floating panel centered within the right column
  // on desktop, since FloatingOverlay only wraps the details area.
  return (
    <div className="event-page relative">
      <div
        className="event-page-cover-desktop"
        style={
          imageUrl
            ? {
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <Header />
        <EventCover {...baseCoverProps} />
      </div>
      <FloatingOverlay panel={floatingPanel} panelVisible={showPanel}>
        <div className="event-page-cover-mobile">
          <Header />
          <EventCover {...baseCoverProps} src={imageUrl} />
        </div>
        <div className="event-page-details">
          <GuestCountBar
            goingCount={goingCount}
            cantGoCount={cantGoCount}
            blurred={!authenticated}
            avatars={avatars}
          />
          <InfoRow
            icon={dateIcon}
            iconBackground="var(--color-violet-50)"
            primary={formatDate(event.date)}
            secondary={formatTimeRange(event.date, event.end_date)}
          />
          {/* Penpot uses #f3f1fe — no exact token; using violet-50 as nearest match */}
          <InfoRow
            icon="📍"
            iconBackground="var(--color-violet-50)"
            primary={event.location_name}
            secondary={event.location_address}
          />
          {event.price != null && event.price > 0 && (
            <InfoRow
              icon={priceIcon}
              iconBackground="var(--color-green-50)"
              primary={`${formatPrice(event.price)} per person`}
            />
          )}
          <Divider />
          <DescriptionBlock>{event.description}</DescriptionBlock>
          <Divider />
          <InfoRow
            icon={
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                {event.creator.first_name.charAt(0)}
                {event.creator.last_name.charAt(0)}
              </span>
            }
            primary={hostName}
            secondary="Host"
          />

          {authenticated && (goingCount > 0 || cantGoCount > 0) && (
            <>
              <Divider />
              {goingCount > 0 && (
                <>
                  <GuestListHeader label="Going" count={goingCount} />
                  {invite?.response === 'yes' && profile && (
                    <GuestListItem
                      name={`${profile.first_name} ${profile.last_name} (You)`}
                      rsvpStatus="rsvp-going"
                      paymentStatus={
                        isCreator && event.price && event.price > 0
                          ? event.my_payment_status === 'paid'
                            ? 'payment-paid'
                            : 'payment-unpaid'
                          : undefined
                      }
                    />
                  )}
                  {goingGuests.map(g => (
                    <GuestListItem
                      key={g.id}
                      name={`${g.first_name} ${g.last_name}`}
                      avatarSrc={g.image_path ?? undefined}
                      rsvpStatus="rsvp-going"
                      paymentStatus={
                        isCreator && event.price && event.price > 0
                          ? g.payment_status === 'paid'
                            ? 'payment-paid'
                            : 'payment-unpaid'
                          : undefined
                      }
                    />
                  ))}
                </>
              )}
              {cantGoCount > 0 && (
                <>
                  <GuestListHeader label="Can't Go" count={cantGoCount} />
                  {invite?.response === 'no' && profile && (
                    <GuestListItem
                      name={`${profile.first_name} ${profile.last_name} (You)`}
                      rsvpStatus="rsvp-cant-go"
                    />
                  )}
                  {cantGoGuests.map(g => (
                    <GuestListItem
                      key={g.id}
                      name={`${g.first_name} ${g.last_name}`}
                      avatarSrc={g.image_path ?? undefined}
                      rsvpStatus="rsvp-cant-go"
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </FloatingOverlay>
    </div>
  )
}
